import { Tab, Tabs } from '@mui/material';
import {
  CompositeFilterDescriptor,
  DataResult,
  DataSourceRequestState,
  toDataSourceRequestString
} from '@progress/kendo-data-query';
import {
  ExcelExport,
  ExcelExportColumnProps
} from '@progress/kendo-react-excel-export';
import { Grid, GridSortChangeEvent } from '@progress/kendo-react-grid';
import axios from 'axios';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RouteAnimation } from 'src/animations/animations';
import { SecondaryButton } from 'src/components/shared/Button/Button';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { TabPanel } from 'src/components/shared/Tabs/TabPanel';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import { alertSuccess } from 'src/store/alerts/alerts.actions';
import { getRequest } from 'src/store/api-handler';
import { selectActiveGrades } from 'src/store/grades/grades.selectors';
import { ParameterSetDto } from 'src/store/parametersets/parameterset';
import { selectParameterSets } from 'src/store/parametersets/parametersets.selectors';
import {
  ProductDetailDto,
  ProductDto,
  ProductNRunDto
} from 'src/store/products/product';
import { mapProductDetailToEdit } from 'src/store/products/product.map';
import {
  copyProduct,
  createProduct,
  deleteProduct,
  editProduct,
  fetchActiveParameterSet,
  fetchFilteredProductParameters,
  fetchFilteredProducts,
  productActions,
  setActiveTab
} from 'src/store/products/products.actions';
import { ProductApi } from 'src/store/products/products.api';
import {
  selectActiveParameterSet,
  selectActiveTab,
  selectFilteredProductParameters,
  selectFilteredProducts,
  selectProductsRequestState,
  selectProductStatuses
} from 'src/store/products/products.selectors';
import { selectProductTypes } from 'src/store/producttypes/producttypes.selectors';
import { isLoadingProducts } from 'src/store/requests/requests.selectors';
import { selectRunSystems } from 'src/store/runsystems/runsystems.selectors';
import { selectSegments } from 'src/store/segments/segments.selectors';
import { download, getFileNameFromHeader } from 'src/utils/download';
import { push } from 'src/utils/navigation';
import { LabelsContainer } from './Product/Labels/LabelsContainer';
import { ProductParametersList } from './ProductParametersList';
import { ProductsList } from './ProductsList';
import { ProductsNRunForm } from './ProductsNRunForm';
import { ProductsRunForm } from './ProductsRunForm';

export const ProductsPage = () => {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Nullable<ProductDto>>(null);
  const [detailedProduct, setDetailedProduct] =
    useState<Nullable<ProductDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showLabelsModal, setShowLabelsModal] = useState<boolean>(false);
  const [showCreateRunModal, setShowCreateRunModal] = useState<boolean>(false);
  const [showNRunModal, setShowNRunModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();
  const [showExportSpinner, setShowExportSpinner] = useState<boolean>(false);
  const [exporter, setExporter] = useState<Nullable<ExcelExport>>(null);
  const grid = useRef<Grid>(null);
  const [run, setRun] = useState<ProductDetailDto>({} as ProductDetailDto);

  const tab = useSelector(selectActiveTab);

  const parameterSets = useSelector(selectParameterSets);
  const activeParameterSet = useSelector(selectActiveParameterSet);
  const filteredProducts = useSelector(selectFilteredProducts);
  const filteredProductParameters = useSelector(
    selectFilteredProductParameters
  );
  const grades = useSelector(selectActiveGrades);
  const runSystems = useSelector(selectRunSystems);
  const productTypes = useSelector(selectProductTypes);
  const segments = useSelector(selectSegments);
  const productStatuses = useSelector(selectProductStatuses);

  const requestState = useSelector(selectProductsRequestState);
  const loadingProducts = useSelector(isLoadingProducts);

  const fetchProducts = useCallback(
    (dataState: DataSourceRequestState) => {
      const queryStr = `${toDataSourceRequestString(dataState)}`;
      dispatch(fetchFilteredProducts(queryStr));
    },
    [dispatch]
  );

  const fetchDetailedProduct = async (id: number) => {
    const response = await ProductApi.getById(id);
    if (response.ok) {
      setDetailedProduct(response.data);
    }
  };

  const fetchProductParameters = useCallback(
    (dataState: DataSourceRequestState) => {
      const queryStr = `${toDataSourceRequestString(dataState)}`;
      if (activeParameterSet) {
        dispatch(
          fetchFilteredProductParameters(activeParameterSet.id, queryStr)
        );
      }
    },
    [activeParameterSet, dispatch]
  );

  useEffect(() => {
    tab === 0
      ? fetchProducts(requestState)
      : fetchProductParameters(requestState);
  }, [tab, requestState, fetchProducts, fetchProductParameters]);

  const onParameterSetChange = async (
    parameterSet: Nullable<ParameterSetDto>
  ) => {
    if (parameterSet) {
      dispatch(fetchActiveParameterSet(parameterSet.id));
    }
  };

  const onSortChange = useCallback(
    (event: GridSortChangeEvent) => {
      dispatch(
        productActions.updateRequestState({
          ...requestState,
          sort: event.sort
        })
      );
    },
    [dispatch, requestState]
  );

  const onFilterChange = useCallback(
    (filter: CompositeFilterDescriptor | undefined) => {
      dispatch(
        productActions.updateRequestState({
          ...requestState,
          filter: filter,
          skip: 0
        })
      );
    },
    [dispatch, requestState]
  );

  const onResetFilters = useCallback(() => {
    dispatch(
      productActions.updateRequestState({
        ...requestState,
        filter: undefined,
        skip: 0
      })
    );
  }, [dispatch, requestState]);

  const onPageChange = useCallback(
    (skip: number, take: number) => {
      dispatch(
        productActions.updateRequestState({
          ...requestState,
          skip,
          take
        })
      );
    },
    [dispatch, requestState]
  );

  const onCreate = useCallback(() => {
    push('/products/create', navigate, RouteAnimation.SlideRight);
  }, [navigate]);

  const onEdit = useCallback(
    (product: ProductDto) => {
      push(`/products/${product.id}`, navigate, RouteAnimation.SlideRight);
    },
    [navigate]
  );

  const onCancel = () => {
    setShowDeleteModal(false);
    setShowNRunModal(false);
    setShowCreateRunModal(false);
    setProduct(null);
  };

  const onCreateRun = () => {
    setRun({} as ProductDetailDto);
    setShowCreateRunModal(true);
    fetchProducts(requestState);
  };

  const onEditRun = async (product: ProductDto) => {
    const response = await ProductApi.getById(product.id);
    if (response.ok) {
      setRun(response.data);
      setShowCreateRunModal(true);
      fetchProducts(requestState);
    }
  };

  const onNRun = () => {
    setShowNRunModal(true);
  };

  const onSubmitCreateRun = async (product: ProductDetailDto) => {
    const dto = mapProductDetailToEdit(product);
    dto.id
      ? await dispatch(editProduct(dto.id, dto))
      : await dispatch(createProduct(dto));
    setShowCreateRunModal(false);
  };

  const onSubmitNRun = async (nRun: ProductNRunDto) => {
    const response = await ProductApi.createNRun(nRun);
    if (response.ok) {
      fetchProducts(requestState);
      setShowNRunModal(false);
      dispatch(alertSuccess(`${nRun.count} ${t('product.nRunCreated')}`));
    }
  };

  const onDelete = useCallback((product: ProductDto) => {
    setShowDeleteModal(true);
    setProduct(product);
  }, []);

  const onConfirmDelete = async () => {
    if (product) {
      await dispatch(deleteProduct(product.id!, product));
      setProduct(null);
      setShowDeleteModal(false);
    }
  };

  const onCopy = useCallback(
    async (product: ProductDto) => {
      const newProductId = await dispatch(copyProduct(product.id));
      push(`/products/${newProductId}`, navigate, RouteAnimation.SlideRight);
    },
    [dispatch, navigate]
  );

  const onPrintLabels = useCallback((product: ProductDto) => {
    setShowLabelsModal(true);
    fetchDetailedProduct(product.id);
  }, []);

  const onDownloadDeliveryNote = useCallback((product: ProductDto) => {
    axios({
      method: 'GET',
      responseType: 'arraybuffer',
      url: `/products/${product.id}/deliverynote`
    })
      .then(response => {
        const disposition = response.headers['content-disposition'];
        const contentType = response.headers['content-type'];
        const filename = getFileNameFromHeader(disposition);
        const blob = new Blob([response.data as Blob], {
          type: contentType
        });
        download(filename, blob);
      })
      .catch(() => {});
  }, []);

  const onInitExporter = useCallback((exporter: ExcelExport) => {
    setExporter(exporter);
  }, []);

  const onExport = async () => {
    setShowExportSpinner(true);
    const newState = { ...requestState, take: undefined, skip: undefined };
    const queryStr = `${toDataSourceRequestString(newState)}`;
    if (tab === 0) {
      const { data } = await getRequest<DataResult>(
        `/products/filtered?${queryStr}`
      );
      const products = data!.data as ProductDto[];
      exporter!.save(
        products,
        grid.current?.columns as ExcelExportColumnProps[]
      );
    } else {
      if (activeParameterSet !== null) {
        await axios({
          method: 'GET',
          responseType: 'arraybuffer',
          url: `/products/productanalyzes/${activeParameterSet.id}?${queryStr}`
        })
          .then(response => {
            const disposition = response.headers['content-disposition'];
            const contentType = response.headers['content-type'];
            const filename = getFileNameFromHeader(disposition);
            const blob = new Blob([response.data as Blob], {
              type: contentType
            });
            download(filename, blob);
          })
          .catch(() => {});
      }
    }
    setShowExportSpinner(false);
  };

  return (
    <Fragment>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('product.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('product.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton
            onClick={onCreateRun}
            icon="la-plus"
            className="mr-32"
          >
            {`${t('create.verb')} ${t('product.run')}`}
          </SecondaryButton>
          <SecondaryButton onClick={onCreate} icon="la-plus" className="mr-32">
            {`${t('create.verb')} ${t('product.singular')}`}
          </SecondaryButton>
          <SecondaryButton
            onClick={onExport}
            loading={showExportSpinner}
            disabled={
              (tab === 0 && !exporter) ||
              (tab === 1 && activeParameterSet === null)
            }
            icon="la-file-excel"
            className="mr-32"
          >
            {`${t('export.excel')}`}
          </SecondaryButton>
          <SecondaryButton onClick={onNRun} icon="la-plus-circle">
            {`${t('create.verb')} ${t('product.nRun')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <Tabs
        value={tab}
        onChange={(_, tab) => dispatch(setActiveTab(tab))}
        aria-label="Products tabs"
      >
        <Tab label={t('product.plural')} />
        <Tab label={t('analysis.plural')} />
      </Tabs>
      <TabPanel index={0} value={tab}>
        <ProductsList
          data={filteredProducts}
          grades={grades}
          runSystems={runSystems}
          productTypes={productTypes}
          segments={segments}
          productStatuses={productStatuses}
          loading={loadingProducts}
          requestState={requestState}
          onEdit={onEdit}
          onEditRun={onEditRun}
          onDelete={onDelete}
          onCopy={onCopy}
          onSortChange={onSortChange}
          onFilterChange={onFilterChange}
          onResetFilters={onResetFilters}
          onPageChange={onPageChange}
          onPrintLabels={onPrintLabels}
          onDownloadDeliveryNote={onDownloadDeliveryNote}
          onInitExporter={onInitExporter}
        />
      </TabPanel>
      <TabPanel index={1} value={tab}>
        <Autocomplete
          label={t('parameterSet.singular')}
          options={parameterSets}
          optionField="name"
          value={activeParameterSet}
          style={{ maxWidth: 600 }}
          className="mb-8"
          onChange={onParameterSetChange}
        />
        <ProductParametersList
          gridRef={grid}
          data={filteredProductParameters}
          loading={loadingProducts}
          requestState={requestState}
          parameterSet={activeParameterSet}
          onEdit={onEdit}
          onSortChange={onSortChange}
          onFilterChange={onFilterChange}
          onPageChange={onPageChange}
        />
      </TabPanel>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('product.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('product.delete')} <q>{product?.runNo}</q>?
        </BodyType>
      </Modal>

      {detailedProduct && (
        <Modal
          width={600}
          header={t('label.singular')}
          padBody={false}
          onClose={() => setShowLabelsModal(false)}
          open={showLabelsModal}
        >
          <LabelsContainer product={detailedProduct!} />
        </Modal>
      )}
      <Modal
        header={`${run.id ? t('edit') : t('create.verb')} ${t('product.run')} ${
          run.id ? run.runNo : ''
        }`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showCreateRunModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <ProductsRunForm
          run={run}
          onCancel={onCancel}
          onSubmit={onSubmitCreateRun}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        header={`${t('create.verb')} ${t('product.nRun')}`}
        confirmText={t('create.verb')}
        onClose={() => onCancel()}
        open={showNRunModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <ProductsNRunForm
          onCancel={onCancel}
          onSubmit={onSubmitNRun}
          ref={callbackRef}
        />
      </Modal>
    </Fragment>
  );
};
